import React, { useRef, useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";

// Detectar pre-renderizado de react-snap
const isReactSnap = typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";

export default function AboutVenue() {
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const userInteracted = useRef(false);

  // Intersection Observer for autoplay on scroll
  useEffect(() => {
    if (isReactSnap) return; // No activar IntersectionObserver durante pre-renderizado
    const video = videoRef.current;
    const section = sectionRef.current;
    if (!video || !section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!userInteracted.current && video.paused) {
              video.muted = true;
              setIsMuted(true);
              video.play().then(() => {
                setIsPlaying(true);
              }).catch(() => {});
            }
          } else {
            if (!video.paused) {
              video.pause();
              setIsPlaying(false);
            }
          }
        });
      },
      { rootMargin: "200px" }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const togglePlayPause = useCallback(() => {
    if (videoRef.current) {
      userInteracted.current = true;
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      userInteracted.current = true;
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted((prev) => !prev);
    }
  }, []);

  // === MODO REACTSNAP: solo mostrar poster, sin video ni interactividad ===
  if (isReactSnap) {
    return (
      <section
        id="venue"
        className="relative py-16 lg:py-24 overflow-hidden"
        data-testid="about-venue-section"
      >
        <div className="container-custom grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="lg:col-span-6"
          >
            <div className="eyebrow mb-6">· Место проведения</div>
            <h2
              className="font-serif font-light leading-[0.95] tracking-tight"
              style={{
                fontSize: "clamp(2.6rem, 5.6vw, 5rem)",
                color: "#F2E9DE",
              }}
              data-testid="venue-name"
            >
              Театр-ресторан
              <br />
              <em style={{ color: "#E57324" }}>Наблюдатель</em>
            </h2>
            <div className="mt-10 flex flex-col gap-6 max-w-xl">
              <p className="text-base lg:text-lg leading-relaxed text-[#F2E9DE]/85">
                Наблюдатель — иммерсивный театр-ресторан, где гастрономия
                и искусство соединяются в единое действие, а гость перестаёт
                быть просто зрителем.
              </p>
              <p className="text-base leading-relaxed text-[#A99F96]">
                Здесь вы проживаете уникальный
                опыт: сюжет разворачивается прямо за вашим столом, а каждое
                блюдо становится продолжением истории. Свет, звук и актёры
                создают атмосферу полного погружения, стирая границу между
                реальностью и сценой.
              </p>
              <div className="pt-8 border-t border-[#F2E9DE]/10 grid grid-cols-2 gap-6">
                <div>
                  <div className="text-[0.68rem] tracking-[0.3em] uppercase text-[#A99F96] mb-2">
                    Адрес
                  </div>
                  <div className="font-serif text-xl text-[#F2E9DE]">
                    г. Москва, ул. Шаболовка 31Г
                  </div>
                </div>
                <div>
                  <div className="text-[0.68rem] tracking-[0.3em] uppercase text-[#A99F96] mb-2">
                    Вместимость
                  </div>
                  <div className="font-serif text-xl text-[#F2E9DE]">
                    100 гостей
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.2 }}
            className="lg:col-span-6 relative"
          >
            <div className="relative w-full h-auto rounded-lg shadow-lg overflow-hidden">
              {/* Solo poster estático durante pre-renderizado */}
              <img
                src="/videos/venue-poster.webp"
                alt="Театр-ресторан Наблюдатель"
                className="w-full h-auto"
                loading="eager"
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse at center, transparent 55%, rgba(8,6,5,0.7) 100%)",
                }}
              />
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  // === MODO NORMAL con video interactivo ===
  return (
    <section
      id="venue"
      ref={sectionRef}
      className="relative py-16 lg:py-24 overflow-hidden"
      data-testid="about-venue-section"
    >
      <div className="container-custom grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        {/* Текст слева (col-span-6) */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="lg:col-span-6"
        >
          <div className="eyebrow mb-6">· Место проведения</div>
          <h2
            className="font-serif font-light leading-[0.95] tracking-tight"
            style={{
              fontSize: "clamp(2.6rem, 5.6vw, 5rem)",
              color: "#F2E9DE",
            }}
            data-testid="venue-name"
          >
            Театр-ресторан
            <br />
            <em style={{ color: "#E57324" }}>Наблюдатель</em>
          </h2>

          <div className="mt-10 flex flex-col gap-6 max-w-xl">
            <p className="text-base lg:text-lg leading-relaxed text-[#F2E9DE]/85">
              Наблюдатель — иммерсивный театр-ресторан, где гастрономия
              и искусство соединяются в единое действие, а гость перестаёт
              быть просто зрителем.
            </p>
            <p className="text-base leading-relaxed text-[#A99F96]">
              Здесь вы проживаете уникальный
              опыт: сюжет разворачивается прямо за вашим столом, а каждое
              блюдо становится продолжением истории. Свет, звук и актёры
              создают атмосферу полного погружения, стирая границу между
              реальностью и сценой.
            </p>

            <div className="pt-8 border-t border-[#F2E9DE]/10 grid grid-cols-2 gap-6">
              <div>
                <div className="text-[0.68rem] tracking-[0.3em] uppercase text-[#A99F96] mb-2">
                  Адрес
                </div>
                <div className="font-serif text-xl text-[#F2E9DE]">
                  г. Москва, ул. Шаболовка 31Г
                </div>
              </div>
              <div>
                <div className="text-[0.68rem] tracking-[0.3em] uppercase text-[#A99F96] mb-2">
                  Вместимость
                </div>
                <div className="font-serif text-xl text-[#F2E9DE]">
                  100 гостей
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Видео справа (col-span-6) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.2 }}
          className="lg:col-span-6 relative"
        >
          <div
            onClick={togglePlayPause}
            className="relative w-full h-auto rounded-lg shadow-lg overflow-hidden cursor-pointer"
          >
            <video
              ref={videoRef}
              className="w-full h-auto"
              poster="/videos/venue-poster.webp"
              preload="none"
              playsInline
              webkit-playsinline
              onEnded={() => setIsPlaying(false)}
              src="/videos/venue-video.mp4"
            />
            {!isPlaying && (
              <button
                className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors"
                aria-label="Reproducir video"
              >
                <svg className="w-16 h-16 text-white opacity-80" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); toggleMute(); }}
              className="absolute bottom-4 right-4 p-2 bg-black/50 hover:bg-black/70 transition-colors rounded-full"
              aria-label={isMuted ? "Включить звук" : "Выключить звук"}
            >
              <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {isMuted ? (
                  <>
                    <path d="M3 9v6h4l5 5V4L7 9H3z"/>
                    <path d="M23 9l-6 6m0-6l6 6"/>
                  </>
                ) : (
                  <>
                    <path d="M3 9v6h4l5 5V4L7 9H3z"/>
                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
                  </>
                )}
              </svg>
            </button>
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse at center, transparent 55%, rgba(8,6,5,0.7) 100%)",
              }}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
